import React, { Component } from 'react';
import classNames from 'classnames';
import autobind from 'class-autobind';

import compact from 'lodash/compact';
import each from 'lodash/each';
import findIndex from 'lodash/findIndex';
import invoke from 'lodash/invoke';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';

import Slider from 'react-slick';

import SlideshowInfoPane from './slideshow_info_pane.jsx';
import SlideshowArrowsNav from './slideshow_arrows_nav.jsx';
import SlideshowThumbnailNav from './slideshow_thumbnails_nav.jsx';

import { isVideoish } from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';


const ANIMATION_SPEED = 400;

const getSelectedSlideIndex = (contentItemAssetPairs, selectedContentItem)=>
  selectedContentItem ? findIndex(contentItemAssetPairs, ['contentItem.uuid', selectedContentItem.uuid]) : 0;


class SlideshowBase_ViewerVersion1 extends Component {

  constructor(props) {
    super(props);

    this.state = {
      isVideoPlaying: false
    };

    this.sliderRef = null;
    
    this.slideChangeCount = 0;

    autobind(this);
  }

  componentWillMount() {
    each(compact(this.props.assetUrlsForPreload), (assetUrl) => {
      (new Image()).src = assetUrl;
    });
  }

  componentWillReceiveProps(nextProps) {
    if (this.sliderRef) {
      this.sliderRef.innerSlider.onWindowResized();
    }

    if (nextProps.selectedContentItem) {
      if (!isEqual(this.props.selectedContentItem.uuid, nextProps.selectedContentItem.uuid)) {
        this.handleGoToSlide(getSelectedSlideIndex(nextProps.contentItemAssetPairs, nextProps.selectedContentItem));
      }
    }
  }

  componentDidMount() {
    this.handleSlideFocus();
  }

  handleSlideFocus() {
    if (this.sliderRef && this.props.isFullPage) {
      this.sliderRef.innerSlider.list.setAttribute('tabindex', 0);
      this.sliderRef.innerSlider.list.focus();
    }
  }

  handleNextClick() {
    this.sliderRef.slickNext();
  }

  handlePrevClick() {
    this.sliderRef.slickPrev();
  }

  handleGoToSlide(slideNum) {
    this.sliderRef.slickGoTo(slideNum);
  }

  updateSlide(oldIndex, newIndex) {
    setTimeout(()=> {

      // we want to ensure we un-mount/re-mount our slideshowitem 
      // on every slideshow item change, 
      // so we use a key which updates after every slideshow change
      this.slideChangeCount += 1;
      
      // Because slick is perhaps the worst piece of software written by humans
      // `afterChange` does not fire reliably enough to depend on. (It, perhaps,
      // is associated with their animation ending detection? I'm not sure.)
      // As such, we rely on the `beforeChange` function and wait ANIMATION_SPEED
      // milliseconds before updating our redux state
      const { contentItemAssetPairs, selectedContentItem, onSelectContentClick } = this.props;
      const newlySelectedContentItem = contentItemAssetPairs[newIndex].contentItem;

      if (newlySelectedContentItem.uuid !== selectedContentItem.uuid) {
        onSelectContentClick(newlySelectedContentItem);

        this.handleSlideFocus();

        if (this.thumbnailsNav) {
          this.thumbnailsNav.determineThumbnailsDisplayFromCurrentSlideIdx(oldIndex, newIndex);
        }
      } else {
        // when does this ever happen? unknown, but we always
        // want to update even if we don't dispatch to redux, so we can update the slideChangeCount
        this.forceUpdate();
      }
      
    }, ANIMATION_SPEED);

  }

  handleSetVideoIsPlaying(result) {
    this.setState({ isVideoPlaying: result });
  }

  render() {
    const { isVideoPlaying } = this.state;
    const {
      SlideshowItemComponent,
      height,
      backgroundStyle,
      sectionId, 
      contentItemAssetPairs,
      selectedContentItem, 
      videoishPlayerProps,
      SlideshowThumbnailComponent,
      onSlideDisplay
    } = this.props;
    
    // Early return in case we have nothing to render
    if (contentItemAssetPairs.length === 0) {
      return <div className="section-wrapper section-type-slideshow" />;
    }

    const contentItems = map(contentItemAssetPairs, 'contentItem');

    const shouldShowCaption = selectedContentItem && (
      (selectedContentItem.caption && selectedContentItem.caption.length > 0) ||
      (selectedContentItem.title  && selectedContentItem.title.length > 0)
    );

    const currentSlideIdx = getSelectedSlideIndex(contentItemAssetPairs, selectedContentItem);

    const atFirstSlide = currentSlideIdx === 0;
    const atLastSlide = currentSlideIdx === (contentItemAssetPairs.length - 1);

    return (
        <div
          style={backgroundStyle}
          data-scroll-to-section-id={sectionId}
          className={classNames('slideshow-wrapper', {
            'at-first-index': atFirstSlide,
            'has-caption': shouldShowCaption,
            'video-playing': isVideoPlaying
          })
        }>

          <div className="galleria-container" style={{ height }}>
            <div className="galleria-stage">
              <Slider
                key={this.slideChangeCount}
                className="galleria-images"
                ref={c => this.sliderRef = c }
                lazyLoad={false}
                infinite={false}
                slidesToShow={1}
                draggable={false}
                slidesToScroll={1}
                initialSlide={currentSlideIdx} // note that React-Slick ignores this on prop update
                speed={ANIMATION_SPEED}
                arrows={false}
                beforeChange={this.updateSlide}
                swipeToSlide={true}
              >

                {
                  contentItemAssetPairs.map(({ contentItem, asset }, idx) => (
                    <SlideshowItemComponent
                      key={contentItem.eid || contentItem.uuid}
                      uuid={contentItem.uuid}
                      contentItem={contentItem}
                      currentSlideIdx={currentSlideIdx}
                      asset={asset}
                      isPlayable={isVideoish(contentItem)}
                      isSelected={currentSlideIdx === idx}
                      onSetVideoPlaying={this.handleSetVideoIsPlaying}
                      onSlideDisplay={() => {
                        onSlideDisplay(contentItem);
                      }}
                      videoishPlayerProps={{
                        ...videoishPlayerProps,
                        onEnded: (videoEventData)=> { 
                          invoke(videoishPlayerProps, 'onEnded', contentItem, videoEventData); 
                        },
                        onError: (videoEventData)=> { 
                          invoke(videoishPlayerProps, 'onError', contentItem, videoEventData); 
                        },
                        onPause: (videoEventData)=> { 
                          invoke(videoishPlayerProps, 'onPause', contentItem, videoEventData); 
                        },
                        onPlay: (videoEventData)=> { 
                          invoke(videoishPlayerProps, 'onPlay', contentItem, videoEventData); 
                        },
                        onSeeked: (videoEventData)=> { 
                          invoke(videoishPlayerProps, 'onSeeked', contentItem, videoEventData); 
                        },
                        onStart: (videoEventData)=> { 
                          invoke(videoishPlayerProps, 'onStart', contentItem, videoEventData); 
                        },
                        onTimeUpdate: (videoEventData)=> { 
                          invoke(videoishPlayerProps, 'onTimeUpdate', contentItem, videoEventData); 
                        }
                      }}
                    />
                  ))
                }

              </Slider>

              <SlideshowArrowsNav
                shouldShowNext={!atLastSlide}
                shouldShowPrev={!atFirstSlide}
                onNext={this.handleNextClick}
                onPrev={this.handlePrevClick}
              />

            </div>

            <SlideshowInfoPane
              isVisible={shouldShowCaption}
              title={selectedContentItem ? selectedContentItem.title : null}
              description={selectedContentItem ? selectedContentItem.renderedCaption : null}
            />
              
            <SlideshowThumbnailNav
              tellRef={(thumbnailsNav) => {
                this.thumbnailsNav = thumbnailsNav;
              }}
              SlideshowThumbnailComponent={SlideshowThumbnailComponent}
              contentItems={contentItems}
              currentSlideIdx={currentSlideIdx}
              onThumbnailClick={this.handleGoToSlide}
            />
          </div>
        </div>
    );
  }

}

export default SlideshowBase_ViewerVersion1;
